import { FederalCheckboxOption, FederalCheckboxOptionWithChildren } from '../../jurisdiction-sources/federal-jurisdictions-mappings'
import { useAppDispatch } from '@/store/store-hooks'
import type { AppDispatch } from '@/store/store'
import { useEffect, useCallback, useRef, useMemo } from 'react'
import { useFederalSelection } from '../../hooks/useFederalSelection'
import { useFederalCourtsData } from '../../hooks/useFederalCourtsData'
import { researchFiltersActions } from '../../store/slice'
import { isFederalCourtCode } from '@/jurisdiction-mappings/jurisdiction-maps'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'

enum CircuitCheckboxStateEnum {
  FALSE = 'false',
  INDETERMINATE = 'indeterminate',
  TRUE = 'true',
}

enum CourtSourceAction {
  ADD = 'add',
  REMOVE = 'remove',
}

/**
 * Helper to dispatch court source actions, splitting codes into caselaw and LRR sources
 * Reduces duplication across handleTopLevelCourtToggle, handleCircuitParentToggle,
 * handleChildCourtToggle, and handleSelectAllToggle
 */
function dispatchCourtSources(dispatch: AppDispatch, conversationId: string, codes: string[], action: CourtSourceAction) {
  const courts = codes.filter((code) => isFederalCourtCode(code))
  const lrrSources = codes.filter((code) => !isFederalCourtCode(code))

  const caselawAction = action === CourtSourceAction.ADD ? researchFiltersActions.addFederalCaselawSources : researchFiltersActions.removeFederalCaselawSources
  const lrrAction = action === CourtSourceAction.ADD ? researchFiltersActions.addFederalLrrSources : researchFiltersActions.removeFederalLrrSources

  if (courts.length > 0) {
    dispatch(caselawAction({ conversationId, sources: courts }))
  }
  if (lrrSources.length > 0) {
    dispatch(lrrAction({ conversationId, sources: lrrSources }))
  }
}

type FederalContentProps = {
  conversationId: string
}

export default function FederalContent(props: FederalContentProps) {
  const { conversationId } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Get federal court options (lazy-loaded from /public)
  const { topLevelFederalCourts, circuitBasedFederalCourts, isLoading, isError } = useFederalCourtsData()

  // Get device type
  const deviceType = useDeviceType()
  const isMobile = deviceType === DEVICE_TYPE.MOBILE

  // Get selected federal courts
  const { selectedFederalCourts } = useFederalSelection(conversationId)

  // Combine all courts and split into 2 columns for balanced layout
  const { column1Courts, column2Courts } = useMemo((): {
    column1Courts: (FederalCheckboxOption | FederalCheckboxOptionWithChildren)[]
    column2Courts: (FederalCheckboxOption | FederalCheckboxOptionWithChildren)[]
  } => {
    const allCourts = [...topLevelFederalCourts, ...circuitBasedFederalCourts]
    const midpoint = Math.ceil(allCourts.length / 2)
    return {
      column1Courts: allCourts.slice(0, midpoint),
      column2Courts: allCourts.slice(midpoint),
    }
  }, [topLevelFederalCourts, circuitBasedFederalCourts])

  /**
   * Toggle top-level court (e.g., Supreme Court, Federal Circuit)
   * These courts have no children and their jurisdiction codes are toggled as a unit
   */
  const handleTopLevelCourtToggle = useCallback(
    (courtJurisdictionCodes: string[]) => {
      const isCurrentlySelected = courtJurisdictionCodes.every((code) => selectedFederalCourts.includes(code))
      const action = isCurrentlySelected ? CourtSourceAction.REMOVE : CourtSourceAction.ADD
      dispatchCourtSources(dispatch, conversationId, courtJurisdictionCodes, action)
      // Listener middleware will automatically sync to requestParams
    },
    [conversationId, selectedFederalCourts, dispatch]
  )

  /**
   * Toggle circuit parent checkbox (e.g., 9th Circuit, 2nd Circuit)
   * Implements three-state cycle:
   * - Click 1 (false → indeterminate): Select Court of Appeals only
   * - Click 2 (indeterminate → true): Select all courts
   * - Click 3 (true → false): Deselect all courts and hide children
   * Checkbox state reflects children selection and syncs with manual child toggles
   */
  const handleCircuitParentToggle = useCallback(
    (allJurisdictionCodes: string[], jurisdictions: Record<string, string | string[]>) => {
      // Calculate current state
      const selectedCount = allJurisdictionCodes.filter((code) => selectedFederalCourts.includes(code)).length
      const totalCount = allJurisdictionCodes.length

      let stateName: CircuitCheckboxStateEnum
      if (selectedCount === 0) {
        stateName = CircuitCheckboxStateEnum.FALSE
      } else if (selectedCount === totalCount) {
        stateName = CircuitCheckboxStateEnum.TRUE
      } else {
        stateName = CircuitCheckboxStateEnum.INDETERMINATE
      }

      if (stateName === CircuitCheckboxStateEnum.FALSE) {
        // Click 1: false → indeterminate - Select only Court of Appeals
        const courtOfAppealsValue = jurisdictions['Court of Appeals']
        if (courtOfAppealsValue) {
          const courtOfAppealsCodes = Array.isArray(courtOfAppealsValue) ? courtOfAppealsValue : [courtOfAppealsValue]
          dispatchCourtSources(dispatch, conversationId, courtOfAppealsCodes, CourtSourceAction.ADD)
        }
      } else if (stateName === CircuitCheckboxStateEnum.INDETERMINATE) {
        // Click 2: indeterminate → true - Select all remaining unselected courts
        const unselectedCodes = allJurisdictionCodes.filter((code) => !selectedFederalCourts.includes(code))
        dispatchCourtSources(dispatch, conversationId, unselectedCodes, CourtSourceAction.ADD)
      } else {
        // Click 3: true → false - Deselect all courts
        dispatchCourtSources(dispatch, conversationId, allJurisdictionCodes, CourtSourceAction.REMOVE)
      }

      // Listener middleware will automatically sync to requestParams
    },
    [conversationId, selectedFederalCourts, dispatch]
  )

  /**
   * Toggle individual child court within a circuit (e.g., C.D. California, W.D. Washington)
   * Handles both single jurisdiction codes and arrays (for bankruptcy courts)
   * Parent checkbox state automatically updates based on children selection
   */
  const handleChildCourtToggle = useCallback(
    (childJurisdictionCodes: string | string[]) => {
      const codes = Array.isArray(childJurisdictionCodes) ? childJurisdictionCodes : [childJurisdictionCodes]
      const isCurrentlySelected = codes.every((code) => selectedFederalCourts.includes(code))
      const action = isCurrentlySelected ? CourtSourceAction.REMOVE : CourtSourceAction.ADD
      dispatchCourtSources(dispatch, conversationId, codes, action)
      // Listener middleware will automatically sync to requestParams
    },
    [conversationId, selectedFederalCourts, dispatch]
  )

  /**
   * Calculate checkbox state for circuit parent based on children selection
   * Returns appropriate state for tri-state checkbox (checked/indeterminate/unchecked)
   */
  const getCircuitCheckboxState = useCallback(
    (allJurisdictionCodes: string[]) => {
      const selectedCount = allJurisdictionCodes.filter((code) => selectedFederalCourts.includes(code)).length
      const totalCount = allJurisdictionCodes.length

      if (selectedCount === 0) return { checked: false, indeterminate: false }
      if (selectedCount === totalCount) return { checked: true, indeterminate: false }
      return { checked: false, indeterminate: true }
    },
    [selectedFederalCourts]
  )

  // Refs for circuit parent checkboxes to set indeterminate state
  const circuitCheckboxRefs = useRef<Record<string, HTMLInputElement | null>>({})

  // Ref for Select All checkbox to set indeterminate state
  const selectAllCheckboxRef = useRef<HTMLInputElement | null>(null)

  // Get all federal jurisdiction codes for Select All functionality
  const allFederalJurisdictionCodes = useMemo(() => {
    const codes: string[] = []
    for (const court of topLevelFederalCourts) {
      codes.push(...court.value.federalCourtJurisdictionCodes)
    }
    for (const circuit of circuitBasedFederalCourts) {
      codes.push(...circuit.value.federalCourtJurisdictionCodes)
    }
    return codes
  }, [topLevelFederalCourts, circuitBasedFederalCourts])

  /**
   * Calculate checkbox state for Select All based on all federal courts selection
   * Returns appropriate state for tri-state checkbox (checked/indeterminate/unchecked)
   */
  const selectAllState = useMemo(() => {
    if (allFederalJurisdictionCodes.length === 0) {
      return { checked: false, indeterminate: false }
    }
    const selectedCount = allFederalJurisdictionCodes.filter((code) => selectedFederalCourts.includes(code)).length

    if (selectedCount === 0) return { checked: false, indeterminate: false }
    if (selectedCount === allFederalJurisdictionCodes.length) return { checked: true, indeterminate: false }
    return { checked: false, indeterminate: true }
  }, [allFederalJurisdictionCodes, selectedFederalCourts])

  /**
   * Toggle all federal courts selection
   * - If none are selected → select all
   * - If some (indeterminate) or all are selected → deselect all
   */
  const handleSelectAllToggle = useCallback(() => {
    // If any are selected (checked or indeterminate), deselect all
    // If none are selected, select all
    const shouldSelectAll = !selectAllState.checked && !selectAllState.indeterminate
    const action = shouldSelectAll ? CourtSourceAction.ADD : CourtSourceAction.REMOVE

    trackEvent(shouldSelectAll ? AnalyticsEvent.RiaSelectAllFederalChecked : AnalyticsEvent.RiaSelectAllFederalUnchecked, {
      conversationId,
    })

    dispatchCourtSources(dispatch, conversationId, allFederalJurisdictionCodes, action)
  }, [conversationId, allFederalJurisdictionCodes, selectAllState, dispatch, trackEvent])

  /**
   * Update indeterminate state for circuit parent checkboxes
   * This effect runs whenever the selection changes to update the visual state
   */
  useEffect(() => {
    circuitBasedFederalCourts.forEach((circuit) => {
      const checkbox = circuitCheckboxRefs.current[circuit.id]
      if (checkbox) {
        const state = getCircuitCheckboxState(circuit.value.federalCourtJurisdictionCodes)
        checkbox.indeterminate = state.indeterminate
      }
    })
  }, [selectedFederalCourts, circuitBasedFederalCourts])

  /**
   * Update indeterminate state for Select All checkbox
   */
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = selectAllState.indeterminate
    }
  }, [selectAllState])

  /**
   * Render a single court item (either top-level or circuit-based)
   */
  const renderCourtItem = useCallback(
    (court: FederalCheckboxOption | FederalCheckboxOptionWithChildren) => {
      // Determine if the court has children by checking if the court has a jurisdictions property with items
      const hasChildren = 'jurisdictions' in court && court.jurisdictions !== null && Object.keys(court.jurisdictions).length > 0

      if (!hasChildren) {
        // Render top-level court (no children)
        const isChecked = court.value.federalCourtJurisdictionCodes.every((code) => selectedFederalCourts.includes(code))
        return (
          <div
            key={court.id}
            className={`h-[52px] md:h-auto mb-0 md:mb-3 flex items-center px-6 md:px-0 ${isMobile ? 'border-b border-brand-neutral-200' : ''}`}
          >
            <input
              id={court.id}
              type="checkbox"
              name={court.id}
              checked={isChecked}
              onChange={() => handleTopLevelCourtToggle(court.value.federalCourtJurisdictionCodes)}
              className="h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 mr-2"
              aria-label={`Select ${court.label}`}
            />
            <label htmlFor={court.id} className="text-sm font-normal text-brand-neutral-950 tracking-[-0.15px]">
              {court.label}
            </label>
          </div>
        )
      }

      // Render circuit-based court (with children)
      const circuitState = getCircuitCheckboxState(court.value.federalCourtJurisdictionCodes)

      return (
        <div key={court.id} className={`mb-0 md:mb-3 ${isMobile ? 'border-b border-brand-neutral-200' : ''}`}>
          {/* Circuit Parent Checkbox */}
          <div className="h-[52px] md:h-auto flex items-center px-6 md:px-0">
            <input
              id={court.id}
              ref={(el) => (circuitCheckboxRefs.current[court.id] = el)}
              type="checkbox"
              name={court.id}
              checked={circuitState.checked}
              onChange={() => handleCircuitParentToggle(court.value.federalCourtJurisdictionCodes, court.jurisdictions)}
              className="h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 mr-2"
              aria-label={`Select ${court.label}`}
            />
            <label htmlFor={court.id} className="text-sm font-normal text-brand-neutral-950 tracking-[-0.15px]">
              {court.label}
            </label>
          </div>

          {/* Circuit Children - Only show when at least one child is selected */}
          {(circuitState.checked || circuitState.indeterminate) && (
            <div className="ml-6 mt-2">
              {'jurisdictions' in court &&
                Object.entries(court.jurisdictions).map(([courtName, jurisdictionCodes]) => {
                  const codes = Array.isArray(jurisdictionCodes) ? jurisdictionCodes : [jurisdictionCodes]
                  const isChildChecked = codes.every((code) => selectedFederalCourts.includes(code))
                  const childId = `${court.id}-${courtName}`

                  return (
                    <div
                      key={courtName}
                      className={`h-[52px] md:h-auto mb-0 md:mb-3 flex items-center px-6 md:px-0 ${isMobile ? 'border-t border-brand-neutral-200' : ''}`}
                    >
                      <input
                        id={childId}
                        type="checkbox"
                        name={childId}
                        checked={isChildChecked}
                        onChange={() => handleChildCourtToggle(jurisdictionCodes)}
                        className="h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 mr-2"
                        aria-label={`Select ${courtName}`}
                      />
                      <label htmlFor={childId} className="text-sm font-normal text-brand-neutral-950 tracking-[-0.15px]">
                        {courtName}
                      </label>
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      )
    },
    [selectedFederalCourts, handleTopLevelCourtToggle, handleCircuitParentToggle, handleChildCourtToggle, getCircuitCheckboxState]
  )
  // Loading state
  if (isLoading) {
    return (
      <div className="md:p-6 flex justify-center items-center py-8">
        <CircularProgressContinuousSized size={24} thickness={4} />
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="md:p-6 px-6 py-4">
        <p className="text-red-600 text-sm">Failed to load federal court options. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="md:p-6">
      {/* Select All Checkbox */}
      <div className="h-[52px] md:h-auto p-2 flex items-center px-6 md:px-0  md:pb-3 mt-0">
        <input
          id="select-all-federal"
          ref={selectAllCheckboxRef}
          type="checkbox"
          name="select-all-federal"
          checked={selectAllState.checked}
          onChange={handleSelectAllToggle}
          className="h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 mr-2"
          aria-label="Select all federal courts"
        />
        <label htmlFor="select-all-federal" className="text-sm font-semibold text-brand-neutral-950 tracking-[-0.15px]">
          Select All
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 md:p-2">
        {/* Column 1 */}
        <fieldset>
          <legend className="sr-only">Select federal courts - Column 1</legend>
          {column1Courts.map((court) => renderCourtItem(court))}
        </fieldset>

        {/* Column 2 */}
        <fieldset>
          <legend className="sr-only">Select federal courts - Column 2</legend>
          {column2Courts.map((court) => renderCourtItem(court))}
        </fieldset>
      </div>
    </div>
  )
}
